import * as React from 'react';
import { styled } from '../../../../../../theme';
import { FormikContext, useFormik } from 'formik';
import { FormikPhoneInput } from '../../../../../../widgets/inputs';
import { useMFAStore } from '../../../../../../hooks/useMFAStore';
import { useSystem } from '../../../../../../hooks/useSystem';
import { CenterFormOrientation, CenterFormWidget } from '../../../../../../widgets/layout/CenterFormWidget';
import { FormLayoutWidget } from '../../../../../../widgets/layout/FormLayoutWidget';
import { GroupedButtons } from '../../../../../../widgets/buttons/GroupedButtons';
import { ButtonType } from '../../../../../../widgets/buttons/ButtonWidget';
import { ConfigureMFANavigationAction, SetupMFASteps } from '../../ConfigureMFADialog';
import { MFABreadCrumb, MFAFormContent } from '../../SharedMFADialogComponents';

export type SMSMFAConfigureWidgetProps = {
  factorId?: string | null;
  next: (factorId: string) => void;
  back: ConfigureMFANavigationAction;
  onCancel: ConfigureMFANavigationAction;
};

type SMSConfigureFormikValues = {
  userMobile: string;
};

namespace S {
  const BASE_LABEL = 'sms-mfa-configure-widget';

  export const Container = styled.div``;

  export const Message = styled.div`
    margin-bottom: 16px;
    label: ${BASE_LABEL}-message;
    font-size: 14px;
    color: ${(p) => p.theme.text.secondary};
    text-align: center;
  `;

  export const PhoneInput = styled(FormikPhoneInput)`
    width: 100%;
  `;
}

export const SMSMFAConfigureWidget: React.FC<SMSMFAConfigureWidgetProps> = (props) => {
  const system = useSystem();
  const mfaStore = useMFAStore();

  const { factorId } = props;

  const model = factorId ? mfaStore.item(factorId) : null;

  const formik = useFormik<SMSConfigureFormikValues>({
    initialValues: {
      userMobile: model?.model.sms_otp_mobile_number || ''
    },
    validateOnBlur: true,
    validate: (values) => {
      const { userMobile } = values;
      if (!userMobile) {
        return { userMobile: 'This is required' };
      }

      if (!/\+[1-9]\d{1,14}/.test(userMobile)) {
        return { userMobile: 'Invalid phone number' };
      }

      return {};
    },
    onSubmit: async (values) => {
      if (props.factorId) {
        // It is already setup
        return props.factorId;
      }

      const client = await system.clientProvisioner.provisionSensitiveUserHubClient();

      try {
        const response = await client.registerSMSUserMFAFactor({
          consented_at: new Date().toISOString(),
          mobile_number: values.userMobile
        });

        const factorModel = mfaStore.upsert(response.factor);
        return factorModel.id;
      } catch (error) {
        console.error(error);
        return system.dialogStore.errorDialog({
          errorMessage: 'Could not configure SMS/Text multi-factor authentication. See console for details.'
        });
      }
    }
  });

  return (
    <FormikContext.Provider value={formik}>
      <CenterFormWidget noLogo={true} primaryLabel={'Configure MFA'} orientation={CenterFormOrientation.ROW}>
        <MFABreadCrumb steps={SetupMFASteps} currentStep={1} />
        <FormLayoutWidget
          sections={[
            {
              key: 'content',
              elements: [
                <MFAFormContent key="content">
                  <S.Message>{`Please provide your phone number\nto receive your security code.`}</S.Message>
                  <S.PhoneInput
                    formik={formik}
                    name={'userMobile'}
                    placeholder="Mobile number"
                    value={formik.values.userMobile}
                    onChange={(value) => {
                      formik.setFieldValue('userMobile', value);
                    }}
                    hasError={!!formik.errors.userMobile}
                  />
                </MFAFormContent>
              ]
            },
            {
              key: 'buttons',
              elements: [
                <GroupedButtons
                  key="buttons"
                  btns={[
                    {
                      label: 'Cancel',
                      type: ButtonType.TERTIARY,
                      action: props.onCancel
                    },
                    {
                      label: 'Back',
                      type: ButtonType.SECONDARY,
                      action: props.back
                    },
                    {
                      label: 'Next',
                      action: async (event, loading) => {
                        loading(true);
                        const factorId = await formik.submitForm();
                        loading(false);
                        if (factorId) {
                          props.next(factorId);
                        }
                      }
                    }
                  ]}
                />
              ]
            }
          ]}
        />
      </CenterFormWidget>
    </FormikContext.Provider>
  );
};
